import { IFormPartnerBasicInfo } from 'legacy/lut/classes/models/partner360/IFormPartnerBasicInfo'
import { requestRecalculateCompleteness } from 'legacy/lut/features/partner360/completeness/api/requestRecalculateCompleteness'
import {
  changeProgramType,
  IProgramType,
  selectFinancialYear,
  selectOfferingsMode,
  selectProgramType,
  selectProgramTypes,
} from 'legacy/lut/features/partner360/configuration'
import { setOtherSpend } from 'legacy/lut/features/partner360/configuration/helpers/setOtherSpend'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Logic, Operator } from 'core-components/types'
import MeasureController from 'core/analytics/MeasureController'
import Logger from 'core/utils/Logger'

import { getJBPPartnerBasicInfoCrud } from '../../../services/partner360/configuration/helpers/getJBPPartnerBasicInfoCrud'
import { ProgramTypes } from '../../models/ProgramTypes'

export enum ActionsPlanTypes {
  Opex = 'Opex',
  MDF = 'MDF',
  DGF = 'DGF',
  MIF = 'MIF',
}

type TUsePartnerInfoParams = {
  partnerId: string
  programType?: string
  /**
   * @deprecated
   * No more needed
   * */
  metrics?: MeasureController
}

type TUsePartnerInfoResult = {
  partnerInfo: IFormPartnerBasicInfo | undefined
  isLoading: boolean
  fyear: number
  /**
   * @deprecated
   * Use `dispatch` with action `requestRecalculateCompleteness`
   * */
  triggerCompleteness: () => void
  reloadPartnerInfo: () => Promise<void>
  /**
   * @deprecated
   * Use `selectProgramTypes` selector with `useSelector`
   * */
  allButtonTypes: IProgramType[]
  buttonTypes: string[]
  buttonCurrType: ProgramTypes
  /**
   * @deprecated
   * Use dispatch(changeProgramType(newType)) from `features/partner360/configuration`.
   * Check example at ProgramTypeButtons, line 284.
   * */
  setButtonCurrType: (programType: string) => void
  canEditPartner: boolean
  isNewOfferings: boolean
}

/**
 * @deprecated
 * */
export const usePartnerInfo = ({ partnerId }: TUsePartnerInfoParams): TUsePartnerInfoResult => {
  const dispatch = useAppDispatch()

  const [partnerInfo, setPartnerInfo] = useState<IFormPartnerBasicInfo>()
  const [isLoading, setIsLoading] = useState(false)

  const programType = useSelector(selectProgramType)
  const fyear = useSelector(selectFinancialYear)
  const programTypesFromStore = useSelector(selectProgramTypes)
  const isNewOfferings = useSelector(selectOfferingsMode)

  const buttonTypes = useMemo(() => {
    return programTypesFromStore.map((it) => it.programType)
  }, [programTypesFromStore])

  const jbpPartnerDataCrud = getJBPPartnerBasicInfoCrud(
    ['PartnerId', partnerId, 'FYear', fyear].join(',')
  )

  const { isPartnerUser } = useUserRoles()

  const canEditPartner = useMemo(() => {
    return !isPartnerUser
  }, [isPartnerUser])

  const setButtonCurrType = useCallback(
    async (value: string) => {
      dispatch(changeProgramType(value as ProgramTypes))
    },
    [dispatch]
  )

  const triggerCompleteness = async () => {
    const parameters = {
      partnerId,
      fYear: fyear,
    }

    return dispatch(requestRecalculateCompleteness(parameters)).unwrap()
  }

  const updatePartnerInfo = useCallback(
    async (year: number) => {
      if (partnerId && year > 0) {
        setIsLoading(true)
        jbpPartnerDataCrud.setFilter({
          logic: Logic.AND,
          filters: [
            {
              field: 'PartnerID',
              operator: Operator.EQUAL,
              value: partnerId,
            },
            {
              field: 'FYear',
              operator: Operator.EQUAL,
              value: year,
            },
          ],
        })
        try {
          const response = await jbpPartnerDataCrud.fetch()

          if (response.items.length) {
            const newItem = {
              ...setOtherSpend(response.items[0]),
            }

            setPartnerInfo({ ...newItem })
          }
        } catch (e) {
          Logger.error('usePartnerInfo updatePartnerInfo:', e)
        } finally {
          setIsLoading(false)
        }
      }
    },
    [partnerId, jbpPartnerDataCrud]
  )

  const reloadPartnerInfo = useCallback(async (): Promise<void> => {
    if (partnerInfo && partnerInfo.EntryId) {
      jbpPartnerDataCrud.removeQueries(true)
      await updatePartnerInfo(fyear)
    }
  }, [partnerInfo, jbpPartnerDataCrud, updatePartnerInfo, fyear])

  useEffect(() => {
    return jbpPartnerDataCrud.subscribe('change', () => {
      if (fyear) {
        updatePartnerInfo(fyear)
      }
    })
  }, [jbpPartnerDataCrud, updatePartnerInfo, fyear])

  useEffect(() => {
    updatePartnerInfo(fyear)
  }, [updatePartnerInfo, fyear])

  return {
    partnerInfo,
    isLoading,
    fyear,
    triggerCompleteness,
    reloadPartnerInfo,
    allButtonTypes: programTypesFromStore,
    buttonTypes,
    buttonCurrType: programType,
    setButtonCurrType,
    canEditPartner,
    isNewOfferings,
  }
}
